import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskWords, Reveal } from "../../components/motion";
import { SectionLabel } from "../../components/SectionLabel";
import { LogoMark } from "../../components/Logo";

const PILLARS = [
  ["Technical consultancy", "Needs assessment, strategy and on-farm crop protection programmes."],
  ["Applied R&D", "Field trials that validate new tools under real growing conditions."],
  ["Knowledge transfer", "Extension that helps growers and advisors adopt practical, science-based solutions."],
];

export const Manifesto = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  const markY = useTransform(scrollYProgress, [0, 1], [80, -80]);
  return (
    <section ref={ref} data-testid="home-manifesto" className="relative overflow-hidden bg-paper py-24 sm:py-36">
      <motion.div style={{ y: markY }} aria-hidden="true" data-testid="manifesto-logo-bg" className="pointer-events-none absolute -left-16 top-10 w-[26rem] text-teal/[0.06] sm:-left-10 sm:w-[38rem] lg:left-[18%] lg:w-[44rem]">
        <LogoMark framed={false} strokeWidth={2.2} className="h-full w-full" />
      </motion.div>
      <div className="relative mx-auto grid max-w-[1440px] gap-16 px-5 sm:px-8 lg:grid-cols-12 lg:px-12">
        <div className="lg:col-span-8">
          <SectionLabel index="01" className="text-moss">Who we are</SectionLabel>
          <MaskWords
            className="mt-10 font-display text-2xl font-light leading-[1.22] tracking-[-0.01em] text-teal sm:text-3xl lg:text-[2.6rem]"
            highlight={["agroecology", "biological", "control", "functional", "biodiversity"]}
            text="We bring agroecology, biological control and functional biodiversity together to make crop protection practical, science-based and resilient."
          />
          <div className="mt-16 grid gap-10 border-t border-teal/15 pt-10 sm:grid-cols-3">
            {PILLARS.map(([t, d], i) => (
              <Reveal key={t} delay={i * 0.1} data-testid={`manifesto-pillar-${i}`}>
                <p className="font-mono text-xs text-moss">0{i + 1}</p>
                <h3 className="mt-3 font-display text-xl text-teal">{t}</h3>
                <p className="mt-3 text-sm leading-relaxed text-teal-ink/80">{d}</p>
              </Reveal>
            ))}
          </div>
        </div>
        <Reveal className="lg:col-span-4 lg:pt-24">
          <div className="relative aspect-[4/5] overflow-hidden rounded-full rounded-b-none">
            <motion.img style={{ y: imgY }} src="/images/lab.jpg" alt="Preparing biological samples in the laboratory" className="absolute inset-0 h-[124%] w-full -translate-y-[10%] object-cover" />
          </div>
          <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.16em] text-teal/80">Applied R&D — from lab bench to field plot</p>
        </Reveal>
      </div>
    </section>
  );
};
